import { RefObject, useState, useEffect, useMemo, useRef } from "react";
import { Database, GitBranch, BookOpen, Code, RefreshCw, Share2 } from "lucide-react";
import { Editor } from "./Editor";
import { QueryToolbar } from "../QueryToolbar";
import { SchemaDocumentationModal } from "../SchemaDocumentationModal";
import type { Dataset } from "../../types";
import { useMobile } from "../../hooks/useMobile";
import { loadAllDomainSchemas } from "../../utils/api/domains";

interface EditorSectionProps {
  sql: string;
  setSql: (sql: string) => void;
  editorHeight: number;
  editorRef: RefObject<HTMLDivElement>;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  datasets?: Dataset[];
  domain?: string;
  showLineage?: boolean;
  showDocumentation?: boolean;
  onDocumentationClick?: () => void;
  // QueryToolbar props
  onRun: () => void;
  onCheck?: () => void;
  onFormat: () => void;
  onReset: () => void;
  onCopy: () => void;
  onDebug?: () => void;
  isCheckLocked?: boolean;
  onCheckLocked?: () => void;
  showCheck?: boolean;
  executionTimeMs?: number | null;
  memoryUsageBytes?: number | null;
  readBytes?: number | null;
  readRows?: number | null;
  writtenBytes?: number | null;
  writtenRows?: number | null;
  resultBytes?: number | null;
  resultRows?: number | null;
  cpuTimeUs?: number | null;
}

export const EditorSection = ({
  sql,
  setSql,
  editorHeight,
  editorRef,
  onMouseDown,
  onTouchStart,
  datasets = [],
  domain,
  showLineage = true,
  showDocumentation = false,
  onDocumentationClick,
  onRun,
  onCheck,
  onFormat,
  onReset,
  onCopy,
  onDebug,
  isCheckLocked = false,
  onCheckLocked,
  showCheck = true,
  executionTimeMs,
  memoryUsageBytes,
  readBytes,
  readRows,
  writtenBytes,
  writtenRows,
  resultBytes,
  resultRows,
  cpuTimeUs,
}: EditorSectionProps) => {
  const [isSchemaModalOpen, setIsSchemaModalOpen] = useState(false);
  const [isLineageModalOpen, setIsLineageModalOpen] = useState(false);
  const [domainDatasets, setDomainDatasets] = useState<Dataset[]>([]);
  const [domainCatalog, setDomainCatalog] = useState<{
    tables: Array<Dataset & { domainName?: string }>;
    relations: Array<[string, string]>;
    domainToTables: Record<string, Dataset[]>;
    domainToRelations: Record<string, Array<[string, string]>>;
  }>({
    tables: [],
    relations: [],
    domainToTables: {},
    domainToRelations: {},
  });
  const isMobile = useMobile();
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);

  // Регистрируем touch-обработчики напрямую на DOM с {passive: false}
  // Используем capture phase для перехвата события до всплытия
  useEffect(() => {
    const handle = resizeHandleRef.current;
    if (!handle || !isMobile) return;
    // Нативные touch listeners больше не нужны: ресайзер работает через React handlers на хэндле,
    // а перемещение обрабатывается в useResize через window listeners.
    return;
  }, [isMobile, onTouchStart]);

  // Фильтруем валидные datasets (объекты с name)
  const validDatasets = useMemo(() => {
    const result: Dataset[] = [];
    for (const ds of datasets) {
      if (typeof ds !== 'string' && ds && typeof ds === 'object' && 'name' in ds && typeof ds.name === 'string') {
        result.push(ds as Dataset);
      }
    }
    return result;
  }, [datasets]);

  // Имена датасетов, переданных как строки
  const datasetNamesFromProps = useMemo(
    () => {
      const result: string[] = [];
      for (const ds of datasets) {
        if (typeof ds === "string") {
          const nameStr: string = ds;
          result.push(nameStr.toLowerCase());
        }
      }
      return result;
    },
    [datasets]
  );
  
  const hasValidDatasets = validDatasets.length > 0;
  const hasDomain = Boolean(domain);

  // Загружаем все доменные схемы через API как общий каталог таблиц
  useEffect(() => {
    const loadDomainCatalog = async () => {
      try {
        const schemas = await loadAllDomainSchemas();
        
        const allTables: Array<Dataset & { domainName?: string }> = [];
        const allRelations: Array<[string, string]> = [];
        const domainToTables: Record<string, Dataset[]> = {};
        const domainToRelations: Record<string, Array<[string, string]>> = {};

        schemas.forEach((schema) => {
          domainToTables[schema.name] = schema.tables;
          domainToRelations[schema.name] = schema.relations;
          allTables.push(...schema.tables.map(t => ({ ...t, domainName: schema.name })));
          allRelations.push(...schema.relations);
        });

        setDomainCatalog({
          tables: allTables,
          relations: allRelations,
          domainToTables,
          domainToRelations,
        });
      } catch (error) {
        console.error("Ошибка при загрузке доменов через API:", error);
      }
    };

    loadDomainCatalog();
  }, []);

  // Для модалки схем/lineage используем доменные данные по текущему domain (как в DataDocs)
  useEffect(() => {
    if (!domain) {
      setDomainDatasets([]);
      return;
    }

    // Используем ту же логику, что и в DataDocs: берем таблицы и отношения напрямую по имени домена
    const domainTables = domainCatalog.domainToTables?.[domain] || [];
    setDomainDatasets(domainTables);
  }, [domain, domainCatalog]);

  // Для Lineage используем datasets из domain, если они есть, иначе из props
  const lineageDatasets = useMemo(() => {
    const result = domainDatasets.length > 0 ? domainDatasets : validDatasets;
    return result;
  }, [domainDatasets, validDatasets]);


  // Если в props datasets пришли строки-идентификаторы, подхватываем соответствующие таблицы из общего каталога (по имени, без привязки к домену)
  const datasetsByNameFromCatalog = useMemo(() => {
    if (datasetNamesFromProps.length === 0) return [];
    return domainCatalog.tables.filter((ds: any) => datasetNamesFromProps.includes(ds.name.toLowerCase())).map(({ domainName, ...ds }: any) => ds);
  }, [datasetNamesFromProps, domainCatalog]);

  // Для автодополнения в редакторе используем: валидные из props -> найденные по именам -> (если есть) доменные
  const editorDatasets = useMemo(() => {
    const merged: Dataset[] = [];
    validDatasets.forEach(ds => merged.push(ds));
    datasetsByNameFromCatalog.forEach(ds => {
      if (!merged.find(item => item.name === ds.name && item.schema === ds.schema)) {
        merged.push(ds);
      }
    });
    domainDatasets.forEach(ds => {
      if (!merged.find(item => item.name === ds.name && item.schema === ds.schema)) {
        merged.push(ds);
      }
    });
    return merged;
  }, [validDatasets, datasetsByNameFromCatalog, domainDatasets]);

  return (
    <>
      <div
        ref={editorRef}
        className={`flex-shrink-0 flex flex-col mt-0 relative w-full ${isMobile ? 'mb-4' : ''}`}
        style={{ height: `${editorHeight}px`, minHeight: `${editorHeight}px`, maxHeight: `${editorHeight}px`, width: '100%', overflow: 'hidden' }}
      >
        {isMobile ? (
          /* Мобильная версия: тулбар сверху, редактор посередине, тулбар снизу */
          <div className={`bg-neutral-light rounded-[15px] flex-1 flex flex-col overflow-hidden relative`} style={{ minHeight: 0, maxHeight: '100%' }}>
            {/* Тулбар форматирования сверху */}
            <div className="flex-shrink-0 bg-neutral-light rounded-t-[15px] px-4 pt-2 pb-2">
              <div className="flex gap-2 items-center justify-end">
                <button
                  onClick={onFormat}
                  className="flex items-center justify-center w-7 h-7 bg-neutral-light text-neutral-text rounded-[5px] hover:bg-neutral-border active:scale-95 transition-transform border border-neutral-border"
                  title="Форматировать SQL-запрос"
                >
                  <Code size={16} />
                </button>
                <button
                  onClick={onReset}
                  className="flex items-center justify-center w-7 h-7 bg-neutral-light text-neutral-text rounded-[5px] hover:bg-neutral-border active:scale-95 transition-transform border border-neutral-border"
                  title="Очистить результаты запроса"
                >
                  <RefreshCw size={16} />
                </button>
                <button
                  onClick={onCopy}
                  className="flex items-center justify-center w-7 h-7 bg-neutral-light text-neutral-text rounded-[5px] hover:bg-neutral-border active:scale-95 transition-transform border border-neutral-border"
                  title="Скопировать ссылку на запрос"
                >
                  <Share2 size={16} />
                </button>
              </div>
            </div>

            {/* Редактор кода посередине */}
            <div className={`flex-1 flex flex-col relative overflow-hidden bg-white`} style={{ minHeight: 0, maxHeight: '100%' }}>
              <Editor value={sql} onChange={setSql} height="100%" datasets={editorDatasets} />
              
              {/* Кнопка Lineage внутри редактора кода (только для мобильной версии) */}
              {hasDomain && showLineage && (
                <div className={`absolute bottom-3 right-3 z-30 flex gap-2`} style={{ pointerEvents: 'auto' }}>
                  <div className="relative group">
                    <button
                      data-onboarding-id="lineage-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsLineageModalOpen(true);
                      }}
                      className="w-10 h-10 flex items-center justify-center rounded-[5px] bg-white hover:bg-primary/10 transition-colors text-neutral-text hover:text-primary"
                      style={{
                        boxShadow: "0px 2px 7px rgba(0, 0, 0, 0.25)"
                      }}
                      aria-describedby="lineage-tooltip"
                    >
                      <GitBranch className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Тулбар действий снизу */}
            <div className="flex-shrink-0 bg-neutral-light rounded-b-[15px]">
              <div data-onboarding-id="query-toolbar">
                <QueryToolbar
                  onRun={onRun}
                  onDebug={onDebug}
                  onCheck={onCheck}
                  onFormat={onFormat}
                  onReset={onReset}
                  onCopy={onCopy}
                  isMobile={isMobile}
                  isCheckLocked={isCheckLocked}
                  onCheckLocked={onCheckLocked}
                  showCheck={showCheck}
                  executionTimeMs={executionTimeMs}
                  memoryUsageBytes={memoryUsageBytes}
                  readBytes={readBytes}
                  readRows={readRows}
                  writtenBytes={writtenBytes}
                  writtenRows={writtenRows}
                  resultBytes={resultBytes}
                  resultRows={resultRows}
                  cpuTimeUs={cpuTimeUs}
                />
              </div>
            </div>
          </div>
        ) : (
          /* Десктоп версия: без изменений */
        <div className={`bg-white rounded-[15px] flex-1 flex flex-col overflow-hidden relative`} style={{ minHeight: 0, maxHeight: '100%' }}>
          {/* Редактор кода */}
          <div className={`flex-1 flex flex-col relative overflow-hidden`} style={{ minHeight: 0, maxHeight: '100%', borderRadius: '15px 15px 0 0' }}>
            <Editor value={sql} onChange={setSql} height="100%" datasets={editorDatasets} />
            
            {/* Кнопки "Схема данных", "Lineage" и "Документация" внутри редактора кода */}
              {((hasValidDatasets || hasDomain || showDocumentation)) && (
                <div className={`absolute bottom-3 right-3 z-30 flex gap-2`} style={{ pointerEvents: 'auto' }}>
                {hasValidDatasets && (
                <div className="relative group">
                  <button
                    data-onboarding-id="schema-docs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSchemaModalOpen(true);
                    }}
                    className="w-12 h-12 flex items-center justify-center rounded-lg bg-white hover:bg-primary/10 transition-colors text-neutral-text hover:text-primary border border-neutral-border hover:border-primary/40 shadow-md"
                    aria-describedby="schema-tooltip"
                  >
                    <Database className="w-6 h-6" />
                  </button>
                  <div
                    id="schema-tooltip"
                    className="absolute right-full top-1/2 -translate-y-1/2 mr-2 hidden group-hover:block bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 whitespace-nowrap pointer-events-none"
                    role="tooltip"
                  >
                    Схема данных
                    <div className="absolute left-full top-1/2 -translate-y-1/2 -ml-1 w-2 h-2 bg-neutral-text rotate-45"></div>
                  </div>
                </div>
              )}
              {hasDomain && showLineage && (
                <div className="relative group">
                  <button
                    data-onboarding-id="lineage-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsLineageModalOpen(true);
                    }}
                      className="w-[42px] h-[42px] flex items-center justify-center rounded-[5px] bg-white hover:bg-primary/10 transition-colors text-neutral-text hover:text-primary"
                    style={{
                      boxShadow: "0px 2px 7px rgba(0, 0, 0, 0.25)"
                    }}
                    aria-describedby="lineage-tooltip"
                  >
                      <GitBranch className="w-6 h-6" />
                  </button>
                    <div
                      id="lineage-tooltip"
                      className="absolute right-full top-1/2 -translate-y-1/2 mr-2 hidden group-hover:block bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 whitespace-nowrap pointer-events-none"
                      role="tooltip"
                    >
                      Открыть Lineage
                      <div className="absolute left-full top-1/2 -translate-y-1/2 -ml-1 w-2 h-2 bg-neutral-text rotate-45"></div>
                    </div>
                </div>
              )}
                {showDocumentation && onDocumentationClick && (
                <div className="relative group">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDocumentationClick();
                    }}
                    className="w-12 h-12 flex items-center justify-center rounded-lg bg-white hover:bg-primary/10 transition-colors text-neutral-text hover:text-primary border border-neutral-border hover:border-primary/40 shadow-md"
                    aria-describedby="documentation-tooltip"
                  >
                    <BookOpen className="w-6 h-6" />
                  </button>
                  <div
                    id="documentation-tooltip"
                    className="absolute right-full top-1/2 -translate-y-1/2 mr-2 hidden group-hover:block bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 whitespace-nowrap pointer-events-none"
                    role="tooltip"
                  >
                    Документация
                    <div className="absolute left-full top-1/2 -translate-y-1/2 -ml-1 w-2 h-2 bg-neutral-text rotate-45"></div>
                  </div>
                </div>
              )}
              </div>
            )}
          </div>

          {/* Toolbar внизу белого контейнера */}
          <div className="flex-shrink-0 bg-neutral-light rounded-b-[15px]">
            <div data-onboarding-id="query-toolbar">
              <QueryToolbar
                onRun={onRun}
                onDebug={onDebug}
                onCheck={onCheck}
                onFormat={onFormat}
                onReset={onReset}
                onCopy={onCopy}
                isMobile={isMobile}
                isCheckLocked={isCheckLocked}
                onCheckLocked={onCheckLocked}
                showCheck={showCheck}
                executionTimeMs={executionTimeMs}
                memoryUsageBytes={memoryUsageBytes}
                readBytes={readBytes}
                readRows={readRows}
                writtenBytes={writtenBytes}
                writtenRows={writtenRows}
                resultBytes={resultBytes}
                resultRows={resultRows}
                cpuTimeUs={cpuTimeUs}
              />
            </div>
          </div>
        </div>
        )}
      </div>

      <div
        ref={resizeHandleRef}
        onMouseDown={(e) => {
          e.stopPropagation();
          onMouseDown(e);
        }}
        onTouchStart={
          isMobile
            ? (e) => {
                // Делаем сессию touch "замкнутой": Monaco не должен видеть move/end без start.
                isResizingRef.current = true;
                e.stopPropagation();
                onTouchStart?.(e);
              }
            : undefined
        }
        onTouchMove={
          isMobile
            ? (e) => {
                if (!isResizingRef.current) return;
                e.stopPropagation();
              }
            : undefined
        }
        onTouchEnd={
          isMobile
            ? (e) => {
                isResizingRef.current = false;
                e.stopPropagation();
              }
            : undefined
        }
        className="w-full cursor-row-resize flex justify-center items-center"
        style={{ 
          height: isMobile ? "24px" : "8px", 
          marginTop: "-2px",
          ...(isMobile
            ? {
                touchAction: "none" as const,
                WebkitTouchCallout: "none" as const,
                userSelect: "none" as const,
                WebkitUserSelect: "none" as const,
              }
            : {}),
        }}
      >
        <div 
          className={`${isMobile ? 'w-[80px] h-[6px]' : 'w-[50px] h-[4px]'} rounded bg-neutral-border hover:bg-primary/40 active:bg-primary transition-colors`}
          style={{
            ...(isMobile ? { touchAction: "none" as const } : {}),
            pointerEvents: 'auto'
          }}
        />
      </div>

      {/* Модальное окно схемы данных */}
      {hasValidDatasets && (
        <SchemaDocumentationModal
          datasets={validDatasets}
          isOpen={isSchemaModalOpen}
          onClose={() => setIsSchemaModalOpen(false)}
        />
      )}
      {/* Модальное окно Lineage (использует SchemaDocumentationModal с relations) */}
      <SchemaDocumentationModal
        datasets={lineageDatasets}
        isOpen={isLineageModalOpen}
        onClose={() => setIsLineageModalOpen(false)}
        relations={domain ? (domainCatalog.domainToRelations?.[domain] || []) : []}
      />
    </>
  );
};

